import React, { useState, useEffect } from "react";

// Custom components
import toastManager from "../../../../helpers/toastManager";
import TagAutoSuggest from "../../../../components/TagAutoSuggest/TagAutoSuggest";
import { Heading1 } from "../../../../components/Headings/Headings";
import {
  SquareIconNavButton,
  TextActionButton,
} from "../../../../components/Buttons/Buttons";

// API imports
import useAxios from "../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../apis/jsonFormatAuth";

import { v4 as uuidV4 } from "uuid";

// Icons
import { ReactComponent as CustomTermsIcon } from "./Icons/custom_terms_icon_300_48.svg";
import { ReactComponent as CosmeticDermatologyIcon } from "./Icons/cosmetic_dermatology_icon_300_48.svg";
import { ReactComponent as ProceduresSurgeriesIcon } from "./Icons/procedures_surgeries_icon_300_48.svg";
import { ReactComponent as PreventiveCareLifestyleIcon } from "./Icons/preventive_care_lifestyle_icon_300_48.svg";
import { ReactComponent as InstrumentsEquipmentIcon } from "./Icons/instruments_equipment_icon_300_48.svg";
import { ReactComponent as DermatologicalConditionsIcon } from "./Icons/dermatological_conditions_icon_300_48.svg";
import { ReactComponent as DermatologicalSymptomsIcon } from "./Icons/dermatological_symptoms_icon_300_48.svg";
import { ReactComponent as DiseasesDermatologicalManifestationsIcon } from "./Icons/diseases_dermatological_manifestations_icon_300_48.svg";
import { ReactComponent as CommonMedicalConditionsIcon } from "./Icons/common_medical_conditions_icon_300_48.svg";
import { ReactComponent as CommonMedicationsIcon } from "./Icons/common_medications_icon_300_48.svg";
import { ReactComponent as CommonVaccinesIcon } from "./Icons/common_vaccines_icon_300_48.svg";
import { ReactComponent as DermatologicalOralMedicationsIcon } from "./Icons/dermatological_oral_medications_icon_300_48.svg";
import { ReactComponent as DermatologicalTopicalMedicationsIcon } from "./Icons/dermatological_topical_medications_icon_300_48.svg";

import { ArrowPathIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const TagLibraryToggleCard = ({
  termsKey,
  toggles,
  setToggles,
  terms,
  libraryTitle,
  libraryDescription,
  LibraryIcon,
}) => {
  const [termsOpen, setTermsOpen] = useState(false);

  const libraryActive = toggles[termsKey];
  const libraryTerms = terms[termsKey];

  const handleLibraryActiveToggle = () => {
    setToggles((prevToggles) => ({
      ...prevToggles,
      [termsKey]: !prevToggles[termsKey],
    }));
  };

  return (
    <div
      className="flex flex-col items-center justify-center w-full p-4
                 rounded-xl border border-neutral-300"
    >
      <div className="flex flex-row items-center justify-between w-full">
        {/* Icon */}
        <div className="flex flex-1 flex-row items-center justify-start w-full">
          <LibraryIcon className="h-32 w-32 fill-neutral-900" />
        </div>
        {/* Toggle switch */}
        <div className="flex flex-1 flex-row items-center justify-end w-full">
          <div
            className={`flex flex-row items-center justify-start w-20 h-10 p-1
                  rounded-3xl select-none transition-all duration-100
                  cursor-pointer
                  ${libraryActive ? "pl-11 bg-green-400" : "pl-1 bg-red-300"}
                 `}
            onClick={handleLibraryActiveToggle}
          >
            <div className={`w-8 h-8 rounded-3xl bg-neutral-50 shadow-lg`} />
          </div>
        </div>
      </div>

      <div className="py-3" />

      <div className="flex flex-col items-start justify-center w-full Xbg-green-500">
        {/* Tag library title */}
        <p className="text-xl font-bold text-neutral-900">{libraryTitle}</p>
        {/* Tag library description */}
        <div className="py-2" />
        <p className="text-base font-normal text-neutral-900">
          {libraryDescription}
        </p>
      </div>

      {/* Terms list */}

      <div className="flex flex-col w-full items-start justify-center">
        <button
          className="flex flex-row items-center justify-end Xml-4 mt-4 Xpx-2 py-1 font-bold
                         text-neutral-700 hover:text-neutral-600 active:text-neutral-900"
          onClick={() => setTermsOpen(!termsOpen)}
        >
          {libraryTerms.length} Terms
          <ChevronDownIcon
            className={`ml-2 h-5 w-5 flex-none transition-all duration-200 ${
              termsOpen && "rotate-180"
            }`}
            aria-hidden="true"
          />
        </button>
        {termsOpen && (
          <div className="flex flex-wrap items-center justify-start w-full py-2">
            {libraryTerms.map((tag, index) => (
              <div
                key={`${uuidV4()}-${index}`}
                className={
                  "mr-2 my-1 rounded-lg px-3 py-1 text-sm font-semibold text-neutral-700 bg-neutral-200"
                }
              >
                {tag}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileTagsScreen = () => {
  const [tags, setTags] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const { response, axiosFetch } = useAxios();

  const librariesData = [
    {
      key: "cosmetic_dermatology",
      icon: CosmeticDermatologyIcon,
      title: "Cosmetic Surgical & Dermatological Procedures",
      description: `This tag library offers a comprehensive collection of cosmetic dermatological 
      procedures and treatments. From aesthetic enhancements like Botox to surgical procedures such as 
      abdominoplasty, use these tags for precise documentation and easy retrieval.`,
    },
    {
      key: "procedures_surgeries",
      icon: ProceduresSurgeriesIcon,
      title: "Dermatological Procedures & Surgeries",
      description: `Dive into this tag library for a curated list of medical procedures and 
      surgeries, ranging from common interventions like appendectomy to specialized treatments like 
      Mohs surgery. These tags assist in categorizing, documenting, and retrieving detailed surgical 
      histories and treatments with precision.`,
    },
    {
      key: "preventive_care_lifestyle",
      icon: PreventiveCareLifestyleIcon,
      title: "Preventative Care Lifestyle Actions",
      description: `Explore this library for tags related to preventive care and 
      lifestyle actions. From routine check-ups to healthful habits, these tags are designed to 
      help you keep track of measures taken to prevent or reduce health risks.`,
    },
    {
      key: "instruments_equipment",
      icon: InstrumentsEquipmentIcon,
      title: "Dermatological Tools",
      description: `Navigate through this tag library for a variety of dermatological 
      tools and equipment. From diagnostic devices to surgical instruments, use these tags to 
      categorize and document tools used in dermatological care.`,
    },
    {
      key: "dermatological_conditions",
      icon: DermatologicalConditionsIcon,
      title: "Dermatological Conditions",
      description: `Utilize this tag library for a vast range of dermatological 
      conditions. From common skin concerns to rare dermatoses, these tags aid in detailing 
      and recalling specific skin-related conditions.`,
    },
    {
      key: "dermatological_symptoms",
      icon: DermatologicalSymptomsIcon,
      title: "Dermatological Symptoms",
      description: `Delve into this tag library for a comprehensive list of symptoms 
      related to skin and hair conditions. Whether it's itchiness, redness, or other signs, use 
      these tags to describe and remember specific dermatological symptoms.`,
    },
    {
      key: "diseases_dermatological_manifestations",
      icon: DiseasesDermatologicalManifestationsIcon,
      title: "Systemic Diseases with Dermatological Manifestations",
      description: `Employ this tag library to find systemic diseases 
      that have dermatological manifestations. These tags are essential for linking internal health 
      concerns that manifest externally on the skin.`,
    },
    {
      key: "common_medical_conditions",
      icon: CommonMedicalConditionsIcon,
      title: "Common Medical Conditions",
      description: `Browse this library for tags pertaining to common medical conditions. 
      Whether it's cardiovascular, respiratory, or any other system, these tags assist in categorizing 
      and noting general health conditions.`,
    },
    {
      key: "common_medications",
      icon: CommonMedicationsIcon,
      title: "Common Medications",
      description: `Access this library for tags related to widely prescribed medications. 
      Covering a broad spectrum of drugs for various ailments, these tags are invaluable for 
      documenting medication histories.`,
    },
    {
      key: "common_vaccines",
      icon: CommonVaccinesIcon,
      title: "Common Vaccines",
      description: `Explore this library for tags related to commonly administered vaccines. 
      From childhood immunizations to seasonal flu shots, these tags help in documenting vaccination 
      histories accurately and comprehensively.`,
    },
    {
      key: "dermatological_oral_medications",
      icon: DermatologicalOralMedicationsIcon,
      title: "Dermatological Oral Medications",
      description: `Turn to this library for tags linked with oral medications 
      prescribed for dermatological conditions. From antibiotics to anti-inflammatories, use these 
      tags to keep track of oral treatments for skin conditions.`,
    },
    {
      key: "dermatological_topical_medications",
      icon: DermatologicalTopicalMedicationsIcon,
      title: "Dermatological Topical Medications",
      description: `Dive into this library for tags associated with topical 
      medications in dermatology. Ointments, creams, gels, or lotions – these tags help in
      documenting the array of external treatments for skin conditions.`,
    },
    {
      key: "custom",
      icon: CustomTermsIcon,
      title: "Custom Terms",
      description: `Use this tag library for personalized suggestions when creating new tags. It's built from the tags 
      in entries you've previously made, combined with any additional tags you've handpicked in the 'Your Tags' section.`,
    },
  ];

  // Initialize toggles using librariesData
  const [toggles, setToggles] = useState(
    librariesData.reduce(
      (acc, library) => ({ ...acc, [library.key]: true }),
      {}
    )
  );

  // Initialize terms using librariesData
  const [terms, setTerms] = useState(
    librariesData.reduce((acc, library) => ({ ...acc, [library.key]: [] }), {})
  );

  const updateStateFromResponse = (tagsDataResponse) => {
    // Update toggles
    const newToggles = { ...toggles };
    librariesData.forEach((library) => {
      // Check if 'enabled_categories' is an array before using 'includes'
      const isEnabled =
        Array.isArray(tagsDataResponse?.enabled_categories) &&
        tagsDataResponse.enabled_categories.includes(library.key);
      newToggles[library.key] = isEnabled;
    });
    setToggles(newToggles);

    // Update terms
    const newTerms = { ...terms };
    librariesData.forEach((library) => {
      // Provide a default empty array in case the key is not found or the value is not an array
      const libraryTerms = Array.isArray(
        tagsDataResponse?.tag_libraries?.[library.key]
      )
        ? tagsDataResponse.tag_libraries[library.key]
        : [];
      newTerms[library.key] = libraryTerms;
    });
    setTerms(newTerms);

    // Set custom tags with error handling
    const customTags = Array.isArray(tagsDataResponse?.tag_libraries?.custom)
      ? tagsDataResponse.tag_libraries.custom
      : [];
    setTags(customTags);

    // Extend tagSuggestions array with tags from each tag library array
    const allTags = librariesData.reduce((acc, library) => {
      const libraryTags = Array.isArray(
        tagsDataResponse?.tag_libraries?.[library.key]
      )
        ? tagsDataResponse.tag_libraries[library.key]
        : [];
      return acc.concat(libraryTags);
    }, []);

    // Remove duplicates
    const uniqueTags = [...new Set(allTags)];

    setTagSuggestions(uniqueTags);
  };

  useEffect(() => {
    if (response?.action_code === "tags_preferences_retrieved") {
      // console.log(response);
      const tagsDataResponse = response?.payload;
      if (tagsDataResponse) {
        updateStateFromResponse(tagsDataResponse);
      }
    }

    const calculateDuration = (
      baseDuration,
      additionalDurationPerList,
      incrementPerTag,
      ...tagLists
    ) => {
      // Start with the base duration
      let duration = baseDuration;

      // Add time for each list that contains items
      tagLists.forEach((list) => {
        if (list.length > 0) {
          duration += additionalDurationPerList; // Add time for non-empty list
          duration += incrementPerTag * list.length; // Add time for each tag
        }
      });

      return duration;
    };

    // Tags have been updated.
    if (response?.action_code === "tags_preferences_updated") {
      const payload = response?.payload;
      const tagsAdded = payload?.tags_added?.join(", ") || "";
      const tagsDeleted = payload?.tags_deleted?.join(", ") || "";
      const tagsNotAdded = payload?.tags_not_added?.join(", ") || "";
      const tagsNotDeleted = payload?.tags_not_deleted?.join(", ") || "";

      // Create message parts only if arrays are not empty
      const messageParts = [
        "Tags and tag preferences have been updated successfully.",
        tagsAdded && `Tags Added: [${tagsAdded}]`,
        tagsDeleted && `Tags Deleted: [${tagsDeleted}]`,
        tagsNotAdded &&
          `Skipped Adding Standard-Library Tags: [${tagsNotAdded}] `,
        tagsNotDeleted &&
          `Skipped Deleting Entry-Linked Tags: [${tagsNotDeleted}]`,
      ].filter(Boolean); // Filter out any empty strings

      // Join the message parts with double newlines
      const message = messageParts.join("\n\n");

      // Dynamically adjust toast duration depending on message.
      const baseDuration = 5000; // Base duration in ms
      const additionalDurationPerList = 250; // Additional duration for each non-empty list
      const incrementPerTag = 100; // Increment per tag in ms
      const duration = calculateDuration(
        baseDuration,
        additionalDurationPerList,
        incrementPerTag,
        tagsAdded,
        tagsDeleted,
        tagsNotAdded,
        tagsNotDeleted
      );

      toastManager("success", "Tags Updated Successfully!", message, duration);
      // Request data so the user can verify changes were saved or not.
      getData();
    }
  }, [response]);

  const getData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "tags/preferences/",
      sendSignal: false,
      requestConfig: {},
    });
  };

  const handleSubmit = () => {
    const payload = {
      enabled_tag_categories: Object.entries(toggles)
        .filter(([key, value]) => value) // Keep only entries with value `true`
        .map(([key, value]) => key), // Extract the key
      custom_classification_tags: tags,
    };

    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "POST",
      url: "tags/preferences/",
      sendSignal: false,
      requestConfig: payload,
    });
  };

  useEffect(() => {
    // Obtain data from API on initial page load.
    getData();
  }, []);

  return (
    <div className="flex flex-col w-full pb-4 md:pb-0 Xbg-red-500">
      <div className="flex flex-col md:flex-row items-center justify-between w-full Xbg-red-500">
        <div className="flex flex-row items-center justify-start w-full Xbg-green-500">
          <Heading1 headingText={"Tags"} />
        </div>
        <div className="flex flex-col space-y-4 md:space-y-0 md:space-x-4 md:flex-row items-center justify-end w-full h-full pt-5 pb-2 Xbg-yellow-500">
          <SquareIconNavButton
            Icon={ArrowPathIcon}
            actionFunction={getData}
            activeColor={"active:bg-cyan-100"}
          />
        </div>
      </div>

      <div className="py-2" />

      <div className="space-y-4">
        {librariesData.map((libraryData) => (
          <TagLibraryToggleCard
            key={`${uuidV4()}`}
            termsKey={libraryData.key}
            toggles={toggles}
            setToggles={setToggles}
            terms={terms}
            libraryTitle={libraryData.title}
            libraryDescription={libraryData.description}
            LibraryIcon={libraryData.icon}
          />
        ))}
      </div>

      <div className="py-2" />

      <TagAutoSuggest
        tags={tags}
        setTags={setTags}
        suggestionList={tagSuggestions}
      />

      <div className="py-3" />

      <div className="flex flex-row items-center justify-end w-full Xbg-red-500">
        <TextActionButton
          prompt={"Update Tags"}
          actionFunction={handleSubmit}
        />
      </div>
    </div>
  );
};

export default ProfileTagsScreen;
